/**
 * Responsibility: arrange one airfield on the ground — its runways, its tower, its parked aircraft, and whether the ground
 * will carry them.
 *
 * Split from `airfields.js`, which decides *which slots hold a field*. That is a different job, and keeping them apart is
 * what makes the split below enforceable rather than a convention.
 *
 * ## A heading may depend on terrain where a position may not
 *
 * Everything here needs the ground. Nothing in `airfields.js` that the reservation calls does — because the reservation has
 * no terrain sampler, and reserves a *circle*, which is rotation-invariant. So the position of a field must come from the
 * rolls alone, while its **orientation is free to be surveyed**. That asymmetry is the reason this module exists.
 *
 * It is what makes airfields possible at all. Aimed at random, only 21% of candidate sites were flat enough for a 760-unit
 * strip; laid along the local contour that rises to 35%, and the runways that do get built are flatter, so their
 * embankments read as earthworks rather than viaducts. The gradient arrives with the elevation from
 * `sampleSurfaceDetail`, so aiming costs no extra sampling.
 *
 * ## Runway headings are constructed, not rejected
 *
 * "Never three in the same direction" could be enforced by rolling headings and retrying, but retrying makes the number of
 * random draws depend on the outcome, which is exactly how a world stops being reproducible. Instead the headings are built
 * so the constraint cannot be violated — see `headingsFor`.
 *
 * **Half a turn, not a full one.** A runway is bidirectional, so directions live on a circle of circumference `π`. Working
 * mod `2π` would let two runways lie exactly on top of each other and call them 180 degrees apart.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { AIRFIELD } from '../config.js';

/** Furthest a runway end can sit from the site centre, in world units. */
const RUNWAY_EXTENT = AIRFIELD.MAX_RUNWAY * (0.5 + AIRFIELD.SECONDARY_OFFSET_RATIO);

/** Furthest the tower can sit from the site centre, in world units. */
const TOWER_EXTENT =
  Math.hypot(
    AIRFIELD.MAX_RUNWAY * Math.max(...AIRFIELD.TOWER_STATIONS.map((station) => Math.abs(station))),
    AIRFIELD.TOWER_OFFSET,
  ) +
  AIRFIELD.TOWER_WIDTH / 2 +
  AIRFIELD.TOWER_CAB_OVERHANG;

/**
 * How far an airfield reaches from its centre, in world units.
 *
 * **Derived from the layout rather than written down**, so changing a runway length or the tower offset cannot leave the
 * reservation and the tile search disagreeing about how big a field is. Lives here because this module owns the dimensions
 * it comes from.
 */
export const SITE_REACH = Math.max(RUNWAY_EXTENT, TOWER_EXTENT);

/**
 * Separation between two runway directions, in radians.
 *
 * **Modulo half a turn**, because a runway is bidirectional. Exported because the tests for the heading rule and the rule
 * itself must not be able to disagree about what "the same direction" means.
 *
 * @returns {number} 0 to `π/2`.
 */
export function headingSeparation(a, b) {
  const wrapped = Math.abs(a - b) % Math.PI;
  return Math.min(wrapped, Math.PI - wrapped);
}

/**
 * Runway headings that are guaranteed to be far enough apart.
 *
 * Gaps are laid end to end around the half-turn circle: each is at least `MIN_HEADING_SEPARATION`, and the slack left over
 * is shared out by the rolls. Because the gaps sum to exactly `π`, the wrap-around gap between the last heading and the
 * first is the final gap in the list, so **it is bounded too** — which is what makes every pair separated, not just
 * neighbouring ones. Any two non-adjacent runways are separated by a sum of gaps in one direction and a sum of gaps in the
 * other, and every gap already clears the minimum.
 *
 * @param {number} base - Heading of the primary runway, radians.
 * @param {number} count - How many runways.
 * @param {Array<number>} rolls - One 0..1 roll per runway, sharing out the slack.
 * @returns {Array<number>} Headings in radians.
 */
function headingsFor(base, count, rolls) {
  const slack = Math.PI - count * AIRFIELD.MIN_HEADING_SEPARATION;

  /*
    Negative slack means the configuration is asking for more runways than can be separated. Cannot happen at the shipped
    values (three at 0.61 radians needs 1.83 of the available 3.14) and is guarded rather than asserted so that raising
    `MIN_HEADING_SEPARATION` degrades to fewer runways instead of producing overlapping ones.
  */
  if (slack < 0) return [base];

  const total = rolls.slice(0, count).reduce((sum, roll) => sum + roll, 0);
  const headings = [];
  let angle = base;

  for (let i = 0; i < count; i += 1) {
    headings.push(angle);

    // An even share when every roll came up zero, rather than a division by zero.
    const share = total > 0 ? rolls[i] / total : 1 / count;
    angle += AIRFIELD.MIN_HEADING_SEPARATION + slack * share;
  }

  return headings;
}

/** How many runways a roll calls for, from the configured weights. */
export function runwayCountFor(roll) {
  const weights = AIRFIELD.RUNWAY_COUNT_WEIGHTS;
  const total = weights.reduce((sum, weight) => sum + weight, 0);

  let running = 0;
  for (let i = 0; i < weights.length && i < AIRFIELD.MAX_RUNWAYS; i += 1) {
    running += weights[i] / total;
    if (roll < running) return i + 1;
  }

  return Math.min(weights.length, AIRFIELD.MAX_RUNWAYS);
}

/** Position along and across a runway, for a world point. Used to test whether something stands on a strip. */
function localTo(runway, x, z) {
  const dx = x - runway.x;
  const dz = z - runway.z;
  const alongX = Math.cos(runway.heading);
  const alongZ = Math.sin(runway.heading);

  return {
    along: dx * alongX + dz * alongZ,
    across: -dx * alongZ + dz * alongX,
  };
}

/** Whether a point is clear of every runway strip, by a margin. */
function clearOfRunways(x, z, runways, margin) {
  return runways.every((runway) => {
    const { along, across } = localTo(runway, x, z);
    if (Math.abs(along) > runway.length / 2 + margin) return true;
    return Math.abs(across) > AIRFIELD.RUNWAY_WIDTH / 2 + margin;
  });
}

/**
 * Where the control tower stands.
 *
 * Offset to one side of the primary runway, at the first of the configured stations that is clear of *every* runway —
 * because **a tower beside the primary can still be sitting on a crossing one**, which is the case a single offset misses.
 * Falls back to just beyond the primary's threshold, which is always outside every strip since the primary is the longest.
 *
 * @returns {{x: number, z: number, heading: number}}
 */
function towerFor(runways, sideRoll) {
  const primary = runways[0];
  const alongX = Math.cos(primary.heading);
  const alongZ = Math.sin(primary.heading);

  /** A point in the primary runway's frame, taken back to world coordinates. */
  const place = (along, across) => ({
    x: primary.x + alongX * along - alongZ * across,
    z: primary.z + alongZ * along + alongX * across,

    // Square to the primary runway, which is what a tower overlooking a field would be.
    heading: primary.heading,
  });

  const side = sideRoll < 0.5 ? -1 : 1;

  for (const station of AIRFIELD.TOWER_STATIONS) {
    const candidate = place(station * primary.length, side * AIRFIELD.TOWER_OFFSET);
    if (clearOfRunways(candidate.x, candidate.z, runways, AIRFIELD.TOWER_CLEARANCE)) return candidate;
  }

  return place(primary.length / 2 + AIRFIELD.TOWER_OFFSET, side * AIRFIELD.TOWER_OFFSET);
}

/**
 * Work out a site's runways, tower and parked aircraft.
 *
 * @param {object} site - From `airfields.js`, carrying the rolls but nothing derived from terrain.
 * @param {number} count - How many runways to lay out. Passed in rather than read off the site because the collector tries
 *   fewer when the ground will not take them all.
 * @param {(x: number, z: number) => {gradientX: number, gradientZ: number}} sampleGroundDetail
 * @returns {{runways: Array<object>, tower: object, aircraft: object|null}}
 */
export function layoutFor(site, count, sampleGroundDetail) {
  const { gradientX, gradientZ } = sampleGroundDetail(site.x, site.z);

  /*
    Perpendicular to the uphill gradient, which is the contour direction. On genuinely flat ground the gradient is tiny and
    this is effectively arbitrary — which is correct, because every direction there is equally level.
  */
  const contour = Math.atan2(gradientX, -gradientZ);
  const headings = headingsFor(contour, count, site.slackRolls);

  const runways = headings.map((heading, i) => {
    // Each runway after the first is shorter, as a main strip and its crosswind ones are in reality.
    const length = site.primaryLength * Math.pow(AIRFIELD.SECONDARY_LENGTH_RATIO, i);

    /*
      And shifted along its own heading, alternating sides, so the strips form a triangle rather than all crossing at one
      point with three centrelines colliding in the middle.
    */
    const offset = i === 0 ? 0 : length * AIRFIELD.SECONDARY_OFFSET_RATIO * (i % 2 === 1 ? 1 : -1);

    return {
      x: site.x + Math.cos(heading) * offset,
      z: site.z + Math.sin(heading) * offset,
      heading,
      length,
    };
  });

  const tower = towerFor(runways, site.towerSideRoll);

  const primary = runways[0];
  const station = (site.aircraftStationRoll * 2 - 1) * AIRFIELD.AIRCRAFT_STATION_RANGE * primary.length;

  const aircraft =
    site.aircraftRoll < AIRFIELD.AIRCRAFT_CHANCE
      ? {
          x: primary.x + Math.cos(primary.heading) * station,
          z: primary.z + Math.sin(primary.heading) * station,

          // Facing either way down the strip, since a parked aircraft has no reason to prefer one.
          heading: primary.heading + (site.aircraftFlipRoll < 0.5 ? 0 : Math.PI),
        }
      : null;

  return { runways, tower, aircraft };
}

/**
 * Every point whose ground height matters: both edges of every runway, at each embankment station.
 *
 * **The same points the embankment is drawn at**, which is what guarantees the surface is never below the ground. Taking
 * the height from the centreline alone would let an edge poke through it on cross-sloping ground.
 */
function footprintOf(layout) {
  const points = [];

  for (const runway of layout.runways) {
    const alongX = Math.cos(runway.heading);
    const alongZ = Math.sin(runway.heading);
    const side = AIRFIELD.RUNWAY_WIDTH / 2;

    for (const across of [-side, side]) {
      for (let i = 0; i < AIRFIELD.SKIRT_STEPS; i += 1) {
        const along = -runway.length / 2 + (runway.length * i) / (AIRFIELD.SKIRT_STEPS - 1);
        points.push([
          runway.x + alongX * along - alongZ * across,
          runway.z + alongZ * along + alongX * across,
        ]);
      }
    }
  }

  return points;
}

/**
 * Whether the ground under a whole field will do, and what height the surface sits at.
 *
 * **All or nothing.** Half an airfield is a broken airfield, not a rarer one, so the whole footprint is checked before
 * anything is emitted.
 *
 * **This is the real flatness test**, and the only one that has to be right. The slope check the collector runs first is a
 * cheap pre-filter over a single point; this measures the relief across the ground every runway actually covers, which is
 * exactly the quantity the embankment budget is about.
 *
 * @param {{runways: Array<object>}} layout
 * @param {(x: number, z: number) => {elevation: number}} sampleGroundDetail
 * @returns {{deckY: number, drop: number}|null} Null when the ground is unusable.
 */
export function fitToGround(layout, sampleGroundDetail) {
  let lowest = Infinity;
  let highest = -Infinity;

  for (const [x, z] of footprintOf(layout)) {
    const { elevation } = sampleGroundDetail(x, z);
    lowest = Math.min(lowest, elevation);
    highest = Math.max(highest, elevation);
  }

  const drop = highest - lowest;
  if (drop > AIRFIELD.MAX_EMBANKMENT) return null;

  /*
    The surface sits just above the highest ground under the field, so the embankment only ever drops. A level surface
    somewhere in the middle would need cuttings as well as banks, which is twice the geometry for no gain.
  */
  return { deckY: highest + AIRFIELD.SURFACE_RISE, drop };
}
